import { dispatchFetchApi, fetchApi } from './thunk'
import dayjs from '@dayjs'
export const GET_EXPERIMENTS = 'GET_EXPERIMENTS'

export const getExperimentsAction = (payload) => {
  return {
    type: GET_EXPERIMENTS,
    payload
  }
}

// Get all experiments for multiple products
export const getAllExperiments = (products, status) => {
  // Accepts products as array or string for backward compatibility
  const productArr = Array.isArray(products) ? products : [products]
  return async (dispatch) => {
    const requests = productArr.map(product =>
      dispatch(fetchApi(`/experiments?product=${product}&status=${status}`))
    )

    const results = await Promise.all(requests)

    // Combine all experiments into one payload
    const allExperiments = results.reduce((acc, res) => {
      if (res?.payload?.experiments) {
        const { ids, entities } = res.payload.experiments
        acc.ids.push(...ids)
        acc.entities = { ...acc.entities, ...entities }
      }
      return acc
    }, { ids: [], entities: {} })
    dispatch(getExperimentsAction({ experiments: allExperiments }))
  }
}

// Get users experiments for one or more products
export const getUsersExperiments = (products = ['exolab'], status) => {
  // Accepts products as array or string for backward compatibility
  const productArr = Array.isArray(products) ? products : [products]
  return async (dispatch) => {
    const requests = productArr.map(product =>
      dispatch(fetchApi(`/experiments/${product}/me?status=${status}`))
    )

    const results = await Promise.all(requests)

    // Combine all experiments into one payload
    const allExperiments = results.reduce((acc, res) => {
      if (res?.payload?.experiments) {
        const { ids, entities } = res.payload.experiments
        acc.ids.push(...ids)
        acc.entities = { ...acc.entities, ...entities }
      }
      return acc
    }, { ids: [], entities: {} })

    dispatch(getExperimentsAction({ experiments: allExperiments }))
  }
}

// Get all experiments marked as isGloballyShared
export const getGlobalExperiments = (productSlug) => {
  return dispatchFetchApi(
    `/experiments/${productSlug}/globally-shared`,
    getExperimentsAction
  )
}

// Get active class experiments
export const getClassExperiments = (classId) => {
  return dispatchFetchApi(
    `/classes/${classId}/experiments`,
    getExperimentsAction
  )
}

// returns only active experiments
function filterExperiments (course, experiments) {
  const { ids, entities } = experiments
  let count = 0

  for (let i = 0; i < ids.length; i++) {
    if (entities[ids[i]].user._id === course.teacher._id) {
      count = count + 1
    }
  }

  return {
    experiments: {
      ids: ids ? ids.filter(id => !entities[id].endTime) : [],
      entities: entities || {},
      count
    }
  }
}

export function checkClassroomExperiments (course) {
  return async (dispatch) => {
    // first check if classroom has active experiments
    const { payload: classPayload } = await dispatch(fetchApi(`/classes/${course._id}/experiments`))
    const classExperiments = filterExperiments(course, classPayload.experiments)
    if (classExperiments.count > 0) {
      dispatch(getExperimentsAction(classExperiments))
    } else {
      // no active experiment in this class: fetch user's experiments for both exolab and virtual-exolab
      const userResults = await Promise.all([
        dispatch(fetchApi('/experiments/exolab/me')),
        dispatch(fetchApi('/experiments/virtual-exolab/me'))
      ])
      const userCombined = userResults.reduce((acc, res) => {
        if (res?.payload?.experiments) {
          const { ids, entities } = res.payload.experiments
          acc.ids.push(...ids)
          acc.entities = { ...acc.entities, ...entities }
        }
        return acc
      }, { ids: [], entities: {} })
      const allExperiments = filterExperiments(course, userCombined)
      dispatch(getExperimentsAction(allExperiments))
    }
  }
}

// Add a new experiment
export const ADD_EXPERIMENT = 'ADD_EXPERIMENT'

export const addExperimentAction = (payload) => {
  return {
    type: ADD_EXPERIMENT,
    payload
  }
}

export function addExperiment (data, classrooms, history, product = 'exolab') {
  const dataPayload = { ...data }
  if (data.device) {
    dataPayload.device = data.device.device
  }
  if (product && dataPayload.product == null) {
    dataPayload.product = product
  }

  const isVirtual = product === 'virtual-exolab'

  return async (dispatch) => {
    const response = await dispatch(fetchApi('/experiments', 'POST', dataPayload))
    if (response?.type !== 'SUCCESS') return undefined

    const { payload } = response
    const _id = payload?.experiment?._id
    if (!_id) return undefined

    for (let i = 0; i < classrooms.length; i++) {
      await dispatch(fetchApi(`/classes/${classrooms[i]}/experiments/${_id}/assign`))
    }

    dispatch(addExperimentAction(payload))

    return _id
  }
}

// Get an experiment's config settings
export const GET_EXPERIMENT_CONFIG = 'GET_EXPERIMENT_CONFIG'

export const getExperimentConfigAction = (payload) => {
  return {
    type: GET_EXPERIMENT_CONFIG,
    payload
  }
}

export const getExperimentConfig = (payload) => {
  return dispatchFetchApi(
    '/api/experiments/config',
    getExperimentConfigAction
  )
}

// Get an experiment
export const GET_EXPERIMENT = 'GET_EXPERIMENT'

export const getExperimentAction = (payload) => {
  return {
    type: GET_EXPERIMENT,
    payload
  }
}

export const getExperiment = (_id) => {
  return dispatchFetchApi(
    `/experiments/${_id}/?includeDataEventDates=true`,
    getExperimentAction
  )
}

// Update an experiment
export const UPDATE_EXPERIMENT = 'UPDATE_EXPERIMENT'

export const updateExperimentAction = (payload) => {
  return {
    type: UPDATE_EXPERIMENT,
    payload
  }
}

export const updateExperiment = (data, id, add, remove, history, isVirtual) => {
  return async (dispatch) => {
    const response = await dispatch(fetchApi(`/experiments/${id}`, 'PUT', data))
    if (response?.type !== 'SUCCESS') return false

    const { payload } = response

    for (let i = 0; i < add.length; i++) {
      await dispatch(fetchApi(`/classes/${add[i]}/experiments/${id}/assign`))
    }

    for (let j = 0; j < remove.length; j++) {
      await dispatch(fetchApi(`/classes/${remove[j]}/experiments/${id}/unassign`))
    }

    dispatch(updateExperimentAction(payload))
    return true
  }
}

// Archives an experiment
export const ARCHIVE_EXPERIMENT = 'ARCHIVE_EXPERIMENT'

export const archiveExperimentAction = (payload) => {
  return {
    type: ARCHIVE_EXPERIMENT,
    payload
  }
}

export const archiveExperiment = (data) => {
  const { _id } = data

  return async (dispatch) => {
    const { payload } = await dispatch(fetchApi(`/experiments/${_id}/archive`, 'POST', data))
    dispatch(archiveExperimentAction(payload))
    return payload
  }
}

// Get experiment data
export const GET_EXPERIMENT_DATA = 'GET_EXPERIMENT_DATA'

export const getExperimentDataAction = (payload) => {
  return {
    type: GET_EXPERIMENT_DATA,
    payload
  }
}

export const getExperimentData = (id, begin, end, sortDirection) => {
  const beginTime = dayjs.utc(begin).valueOf()
  const endTime = dayjs.utc(end).valueOf()
  return dispatchFetchApi(
    `/experiments/${id}/data?begin=${beginTime}&end=${endTime}&sortDirection=${sortDirection || ''}`,
    getExperimentDataAction
  )
}

export function getExperimentByDay (id, day) {
  return fetchApi(`/experiments/${id}/days/${day}/data`)
}

// set time range
export const SET_TIME_RANGE = 'SET_TIME_RANGE'

export const setTimeRange = (start, end) => {
  return {
    type: SET_TIME_RANGE,
    payload: {
      start,
      end
    }
  }
}
